'use client';

import * as React from 'react';
import { useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Plus, Pencil, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useIntegrationsList, useUpsertIntegration, useDeleteIntegration } from '@/lib/api/integrations';
import { useToast } from '@/components/ui/use-toast';
import { useStoreNavigate } from '@/hooks/use-app-navigate';
import { CustomPaymentForm } from '@/components/integrations/forms/custom-payment-form';
import type { Integration } from '@/lib/api/integrations';

type ViewState =
  | { mode: 'list' }
  | { mode: 'new' }
  | { mode: 'edit'; integration: Integration };

import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';

export default withRequiredPermission(CustomPaymentHubPage, PERMISSIONS.STORES_MANAGE);

function CustomPaymentHubPage() {
  const { id: storeId } = useParams<{ id: string }>();
  const { storeRoutes, navigate } = useStoreNavigate();
  const { toast } = useToast();

  const [view, setView] = React.useState<ViewState>({ mode: 'list' });
  const [deleteDialog, setDeleteDialog] = React.useState<{ open: boolean; integration: Integration | null }>({
    open: false,
    integration: null,
  });
  const [isSaving, setIsSaving] = React.useState(false);

  const { data: allIntegrations, isLoading } = useIntegrationsList(storeId!);
  const upsertMutation = useUpsertIntegration();
  const deleteMutation = useDeleteIntegration();

  const instances = React.useMemo(
    () => (allIntegrations ?? []).filter((i) => (i.provider as string) === 'custom_payment'),
    [allIntegrations],
  );

  const handleSave = async (formData: Record<string, any>) => {
    setIsSaving(true);
    try {
      const isEdit = view.mode === 'edit';
      await upsertMutation.mutateAsync({
        storeId: storeId!,
        integration: {
          id: isEdit ? view.integration.id : undefined,
          provider: 'custom_payment',
          type: 'payment',
          name: formData.displayName,
          logoUrl: formData.logoUrl || null,
          enabled: isEdit ? view.integration.enabled : true,
          credentials: formData.credentials ?? {},
          settings: formData.settings ?? {},
        },
      });
      setView({ mode: 'list' });
    } catch {
      // error toast handled by mutation
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleEnabled = async (integration: Integration) => {
    try {
      await upsertMutation.mutateAsync({
        storeId: storeId!,
        integration: {
          id: integration.id,
          provider: 'custom_payment',
          type: 'payment',
          name: integration.name,
          logoUrl: integration.logoUrl,
          enabled: !integration.enabled,
          credentials: integration.credentials ?? {},
          settings: integration.settings ?? {},
        },
      });
    } catch {
      // error toast handled by mutation
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.integration) return;
    try {
      await deleteMutation.mutateAsync({ integrationId: deleteDialog.integration.id });
      toast({ title: 'Deleted', description: 'Payment method removed.' });
    } catch {
      // error toast handled by mutation
    } finally {
      setDeleteDialog({ open: false, integration: null });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // ── Edit / New form view ──────────────────────────────────────────────────
  if (view.mode === 'edit' || view.mode === 'new') {
    const editing = view.mode === 'edit' ? view.integration : null;
    return (
      <div className="flex flex-col space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => setView({ mode: 'list' })} className="w-fit">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to payment methods
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{editing ? `Edit: ${editing.name}` : 'New custom payment method'}</CardTitle>
            <CardDescription>
              Define the payment name, logo, checkout instructions, and thank-you copy that will be
              shown to customers.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <CustomPaymentForm
              defaultValues={
                editing
                  ? {
                      displayName: editing.name,
                      logoUrl: editing.logoUrl ?? '',
                      settings: editing.settings ?? {},
                    }
                  : {}
              }
              onSubmit={handleSave}
              isLoading={isSaving}
            />
            <div className="mt-6 flex justify-end border-t pt-6">
              <Button
                type="submit"
                form="integration-config-form"
                disabled={isSaving}
              >
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {editing ? 'Save changes' : 'Create payment method'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── List view ─────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(storeRoutes.INTEGRATIONS())} className="w-fit">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to integrations
          </Button>
        </div>
        <Button onClick={() => setView({ mode: 'new' })}>
          <Plus className="mr-2 h-4 w-4" />
          Add payment method
        </Button>
      </div>

      <div className="space-y-2">
        <h1 className="text-2xl font-semibold">Custom payment methods</h1>
        <p className="text-muted-foreground text-sm">
          Each entry appears as a separate option at checkout. You can add as many as you need
          (Venmo, wire transfer, COD, etc.).
        </p>
      </div>

      {instances.length === 0 ? (
        <div className="flex min-h-[300px] flex-col items-center justify-center rounded-lg border border-dashed p-8 text-center">
          <div className="bg-muted flex h-16 w-16 items-center justify-center rounded-full">
            <Plus className="text-muted-foreground h-8 w-8" />
          </div>
          <h3 className="mt-4 text-lg font-semibold">No payment methods yet</h3>
          <p className="text-muted-foreground mb-4 mt-2 text-sm">
            Add your first custom payment method to offer it at checkout.
          </p>
          <Button onClick={() => setView({ mode: 'new' })}>
            <Plus className="mr-2 h-4 w-4" />
            Add payment method
          </Button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {instances.map((instance) => (
            <Card key={instance.id} className="flex flex-col">
              <CardContent className="flex flex-1 flex-col gap-4 p-5">
                {/* Top row: avatar + name + badge */}
                <div className="flex items-start gap-3">
                  {instance.logoUrl ? (
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border bg-white p-1.5 shadow-sm">
                      <img src={instance.logoUrl} alt="" className="h-full w-full object-contain" />
                    </div>
                  ) : (
                    <div className="bg-muted flex h-12 w-12 shrink-0 items-center justify-center rounded-lg">
                      <span className="text-muted-foreground text-xl font-semibold leading-none">
                        {instance.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold leading-tight">{instance.name}</p>
                    <Badge
                      variant={instance.enabled ? 'default' : 'secondary'}
                      className={`mt-1.5 text-xs ${instance.enabled ? 'border-green-200 bg-green-500/10 text-green-700' : ''}`}
                    >
                      {instance.enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t" />

                {/* Actions */}
                <div className="flex items-center justify-between">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleEnabled(instance)}
                    disabled={upsertMutation.isPending}
                    className="gap-1.5 text-xs"
                  >
                    {instance.enabled ? (
                      <ToggleRight className="h-4 w-4 text-green-600" />
                    ) : (
                      <ToggleLeft className="text-muted-foreground h-4 w-4" />
                    )}
                    {instance.enabled ? 'Disable' : 'Enable'}
                  </Button>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      title="Edit"
                      onClick={() => setView({ mode: 'edit', integration: instance })}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      title="Delete"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setDeleteDialog({ open: true, integration: instance })}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete confirmation */}
      <AlertDialog
        open={deleteDialog.open}
        onOpenChange={(open) => {
          if (!open) setDeleteDialog({ open: false, integration: null });
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove "{deleteDialog.integration?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This payment method will be removed from checkout immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Removing…' : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
